import { getCreditCardLogo } from "@common-components/payment-form/payment-form";
import { Accordion, AccordionDetails, AccordionSummary, Button, Divider, Typography } from "@mui/material";
import { Box, Stack } from "@mui/system";
import { subscriptionSelectOneObjectByQuery } from "@common-reducers/DBServiceThunks";
import { selectUser } from "@common-reducers/UserSelectors";
import { useAppSelector } from "@common-reducers/hooks/store.hook";
import moment from "moment";
import { useLocation } from 'react-router-dom';

export interface GroupedTransaction {
    transaction: ChargebeeTransaction | null;
    refunds: ChargebeeTransaction[];
}

interface Props {
    group: GroupedTransaction;
    index: number;
    handleOpenModal: (event: React.MouseEvent<HTMLButtonElement>, invoiceId: string) => void
}

const GroupedChargebeeTransaction: React.FC<Props> = ({ group, index, handleOpenModal }) => {
    const hasRefunds = group.refunds.length > 0;
    const panelId = `panel${index}`;
    const user = useAppSelector(selectUser);
    const transaction = group.transaction;
    const subscription = useAppSelector((state) => subscriptionSelectOneObjectByQuery(state.DBSubscriptionReducer, { subscription_id: transaction?.subscription_id ?? '' }));

    const planName = subscription?.plan_id;
    const paymentMethodDetails: PaymentMethodDetails | null = transaction?.payment_method_details
        ? JSON.parse(transaction.payment_method_details)
        : null;
    const location = useLocation();

    const buildCreditCard = () => {
        return <div
            style={{
                display: "flex",
                gap: "10px",
                alignItems: "center",
                width: 140
            }}
        >
            <Stack direction="row" alignItems="center" sx={{ width: 40, border: '1px solid var(--color-text)1a' }}>
                <img style={{
                    height: '100%',
                    width: '100%',
                    objectFit: 'cover'
                }} src={getCreditCardLogo(paymentMethodDetails?.card.brand ?? '')} alt="" />
            </Stack>
            {<span>****</span>}
            <span>{paymentMethodDetails?.card.last4}</span>
        </div>
    }

    if (!transaction) {
        return null;
    }

    let paymentMethodElement = null;
    switch (transaction.payment_method) {
        case 'card':
            paymentMethodElement = buildCreditCard();
            break;
    }

    const buildPaypal = (_invoice: ChargebeeTransaction) => {
        return <div
            style={{
                display: "flex",
                gap: "10px",
                alignItems: "center",
                width: 140
            }}
        >
            <Stack direction="row" alignItems="center" sx={{ width: 40, border: '1px solid var(--color-text)1a' }}>
                <img style={{
                    height: '100%',
                    width: '100%',
                    objectFit: 'cover'
                }} src={getCreditCardLogo('Paypal')} alt="" />
            </Stack>
        </div>
    }

    return <Accordion
        expanded={group.refunds.length > 0}
        sx={{
            "& .MuiAccordionSummary-content": {
                cursor: 'default',
            },
        }}
    >
        <AccordionSummary
            aria-controls={`${panelId}bh-content`}
            id={`${panelId}bh-header`}
        >
            <Stack>
                {planName && (
                    <Box>
                        {planName}
                    </Box>
                )}
                {planName && <Divider sx={{ my: 1 }} />}
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(7, 1fr)',
                        alignItems: 'center',
                        gap: 2,
                    }}
                >
                    <div style={{ userSelect: 'text' }}>{transaction.id}</div>
                    <Box sx={{ width: 90 }}>{moment.utc(transaction.date).format('DD-MMM-YY')}</Box>
                    {paymentMethodElement}
                    <div style={{ textAlign: 'center' }}>{(transaction.amount ?? 0) / 100}</div>
                    <div style={{ textAlign: 'center' }}>{transaction.currency_code}</div>
                    <Button
                        sx={{
                            cursor: 'pointer',
                            margin: '0',
                            textDecoration: 'none',
                            borderRadius: '12px',
                            backgroundColor: '#e8f8fd',
                            color: 'var(--color-accent)',
                            fontSize: '12px',
                            fontWeight: '600',
                            lineHeight: '16px',
                            padding: '6px',
                            textAlign: 'center',
                            textTransform: 'capitalize',
                        }}
                        onClick={
                            () => alert('Implement chargebee invoice view here')
                        }
                    >
                        View
                    </Button>
                    {(user?.role === 'support' || user?.role === 'admin') && (
                        <Button
                            sx={{
                                cursor: 'pointer',
                                margin: '0',
                                textDecoration: 'none',
                                borderRadius: '12px',
                                backgroundColor: '#e8f8fd',
                                color: 'var(--color-accent)',
                                fontSize: '12px',
                                fontWeight: '600',
                                lineHeight: '16px',
                                padding: '6px',
                                textAlign: 'center',
                                textTransform: 'capitalize',
                            }}
                            onClick={(e) => handleOpenModal(e, transaction.id)}
                        >
                            Refund
                        </Button>
                    )}
                </Box>
            </Stack>
        </AccordionSummary>
        {hasRefunds && (
            <AccordionDetails>
                <Typography component="div">
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {group.refunds.length > 0 && (
                            <Box>
                                <b>Refunds:</b>
                                {group.refunds.map((refund, refundIndex) => {
                                    const refundPaymentDetails: PaymentMethodDetails | null = refund.payment_method_details
                                        ? JSON.parse(refund.payment_method_details)
                                        : null;
                                    return (
                                    <Box
                                        key={refundIndex}
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: 'repeat(7, 1fr)',
                                            alignItems: 'center',
                                            py: 2,
                                            px: 0,
                                            gap: 2,
                                            borderBottom: '1px solid lightgrey',
                                        }}
                                    >
                                        <div>{refund.id}</div>
                                        <div>{moment.utc(refund.date).format('DD-MMM-YY')}</div>
                                        <div
                                            style={{
                                                display: 'flex',
                                                gap: '10px',
                                                alignItems: 'center',
                                            }}
                                        >
                                            <Stack direction="row" alignItems="center" sx={{ width: 40, border: '1px solid var(--color-text)1a' }}>
                                                <img
                                                    style={{
                                                        height: '100%',
                                                        width: '100%',
                                                        objectFit: 'cover',
                                                    }}
                                                    src={getCreditCardLogo(refundPaymentDetails?.card.brand ?? '')}
                                                    alt=""
                                                />
                                            </Stack>
                                            <span>{refundPaymentDetails?.card.last4}</span>
                                        </div>
                                        <div style={{ textAlign: 'center' }}>{(refund.amount ?? 0) / 100}</div>
                                        <div style={{ textAlign: 'center' }}>{refund.currency_code}</div>
                                        <Button
                                            sx={{
                                                cursor: 'pointer',
                                                margin: '0',
                                                textDecoration: 'none',
                                                borderRadius: '12px',
                                                backgroundColor: '#e8f8fd',
                                                color: 'var(--color-accent)',
                                                fontSize: '12px',
                                                fontWeight: '600',
                                                lineHeight: '16px',
                                                padding: '6px',
                                                textAlign: 'center',
                                                textTransform: 'capitalize',
                                            }}
                                            onClick={() => window.open(`/invoices/${refund.id}${location.search}`, '_blank')}
                                        >
                                            View
                                        </Button>
                                    </Box>
                                    );
                                })}
                            </Box>
                        )}
                    </Box>
                </Typography>
            </AccordionDetails>
        )}
    </Accordion>
}

export default GroupedChargebeeTransaction;